import React from 'react';

import { Box, Button, Switch, TextField, Typography } from '@mui/material';

import { ImageObject } from '../../pages/types';

interface SingleImageEditorProps {
  image?: string | ImageObject;
  imageWidth?: string;
  imageHeight?: string;
  showCaption?: boolean;
  onPropChange: (key: string, value: any) => void;
  openFileSelector: () => void;
}

const SingleImageEditor: React.FC<SingleImageEditorProps> = ({
  image,
  imageWidth,
  imageHeight,
  showCaption,
  onPropChange,
  openFileSelector,
}) => {
  // Helper function to get image URL from string or ImageObject
  const getImageUrl = (img: string | ImageObject | undefined): string => {
    if (!img) return '';
    return typeof img === 'object' ? img.url : img;
  };

  return (
    <>
      <Typography variant="h6">Content Options</Typography>
      <Box sx={{ mb: 2 }}>
        {image ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <img
              src={getImageUrl(image)}
              alt={typeof image === 'object' ? image.altText : 'Image preview'}
              style={{
                maxWidth: '100%',
                maxHeight: '200px',
                marginBottom: '8px',
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={openFileSelector}
              >
                Change Image
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => onPropChange('image', null)}
              >
                Remove Image
              </Button>
            </Box>
            {typeof image === 'object' && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <TextField
                  label="Image Title/Caption"
                  value={image.title || ''}
                  onChange={(e) => {
                    const updatedImage = {
                      ...image,
                      title: e.target.value,
                    };
                    onPropChange('image', updatedImage);
                  }}
                  fullWidth
                  margin="dense"
                />
                <TextField
                  label="Alt Text"
                  value={image.altText || ''}
                  onChange={(e) => {
                    const updatedImage = {
                      ...image,
                      altText: e.target.value,
                    };
                    onPropChange('image', updatedImage);
                  }}
                  fullWidth
                  margin="dense"
                />
              </Box>
            )}
          </Box>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={openFileSelector}
          >
            Select Image
          </Button>
        )}
      </Box>

      <TextField
        label="Image Width"
        value={imageWidth || '100%'}
        onChange={(e) => onPropChange('imageWidth', e.target.value)}
        fullWidth
        margin="normal"
        helperText="E.g., '100%', '500px', etc."
      />

      <TextField
        label="Image Height"
        value={imageHeight || 'auto'}
        onChange={(e) => onPropChange('imageHeight', e.target.value)}
        fullWidth
        margin="normal"
        helperText="E.g., 'auto', '300px', etc."
      />

      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
        <Typography>Show Caption: </Typography>
        <Switch
          checked={showCaption !== false}
          onChange={(e) => onPropChange('showCaption', e.target.checked)}
        />
      </Box>
    </>
  );
};

export default SingleImageEditor;
